import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Icon, type IconName } from '../components/icons';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { CallNote } from './CallNote';
import { fmtDateTime } from './format';
import { statusSummary, useLeadStatusLabels } from './leadStatus';

const typeIcons: Record<string, IconName> = {
  CALL: 'phone',
  APPOINTMENT_CREATED: 'calendar',
  APPOINTMENT_CHANGED: 'calendar',
  APPOINTMENT_CANCELED: 'calendarX',
  TASK_CREATED: 'clock',
  TASK_UPDATED: 'clock',
  TASK_COMPLETED: 'check',
  TICKET_CREATED: 'message',
  TICKET_UPDATED: 'message',
  EMAIL_SENT: 'mail',
  EMAIL_RECEIVED: 'mail',
  NOTE: 'note',
  DOCUMENT: 'note',
  AI_ANALYSIS: 'sparkle',
  AI_UPDATE: 'sparkle',
  ADDRESS_CHANGED: 'pin',
  DATA_CHANGED: 'edit',
  ASSIGNMENT_CHANGED: 'user',
  CONTACT_POLICY: 'ban',
  PRIVACY: 'lock',
  DEAL: 'award',
  LEAD_CREATED: 'plus',
};

// LeadTimeline: zentrale, chronologische Historie eines Leads (neueste zuerst).
export function LeadTimeline({ leadId, limit }: { leadId: string; limit?: number }) {
  const { project, can } = useProject();
  const [all, setAll] = useState(false);
  const papi = useProjectApi();
  const statusLabels = useLeadStatusLabels();
  const timeline = useQuery({
    queryKey: projectKeys.timeline(project.id, leadId),
    queryFn: ({ signal }) => papi.activities(leadId, signal),
  });
  if (timeline.isPending) return <p className="muted">Historie wird geladen …</p>;
  if (timeline.isError) return <p className="muted">Historie nicht verfügbar.</p>;
  if (!timeline.data.items.length) return <p className="muted">Noch keine Einträge.</p>;
  const items = timeline.data.items;
  const shown = limit && !all ? items.slice(0, limit) : items;
  return (
    <>
      <ol className="timeline" aria-label="Historie">
        {shown.map((a) => (
          <li key={a.id}>
            <span className="timeline-icon" aria-hidden="true">
              <Icon name={typeIcons[a.type] ?? 'dot'} size={16} />
            </span>
            <div>
              <time className="muted">{fmtDateTime(a.occurred_at)}</time>
              <p>{a.summary ? statusSummary(a.summary, statusLabels) : a.type}</p>
              {a.type === 'AI_ANALYSIS' && a.call_id && can('ai.review') && <CallNote callId={a.call_id} />}
            </div>
          </li>
        ))}
      </ol>
      {shown.length < items.length && (
        <button type="button" className="btn btn-small btn-ghost" onClick={() => setAll(true)}>
          Alle {items.length} Einträge anzeigen
        </button>
      )}
    </>
  );
}
